import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ChatSidebar from "../../components/ChatComponents/ChatSideBar";
import { ChatInterface } from "../../components/ChatComponents/ChatInterface";
import LoginModal from "../../components/Modal/LoginModal";
import SignUpModal from "../../components/Modal/SignUpModal";
import FullLoader from "../../components/Loader/FullLoader";
import { getApiKey, setUser } from "../../helpers";
import { GetUserInfo } from "../../services";

const TOKENS_PER_MILLION = 1000000;
const CREDITS_PER_MILLION_TOKENS = 19.11;

const AITutor = () => {
  const history = useHistory();
  const location = useLocation();

  const [loginModal, setLoginModal] = useState(!getApiKey());
  const [signupModal, setSignupModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // True only when we start out already logged in — the guest login/signup
  // flow already fetches a fresh profile itself (see handleAuthenticated), so
  // this covers the "direct/already-authenticated visit" case instead.
  const [userInfoLoading, setUserInfoLoading] = useState(getApiKey());
  const [user, setUserProfile] = useState(() =>
    JSON.parse(sessionStorage.getItem("app_user"))
  );

  // Refresh the profile straight from the API on every mount (rather than trusting
  // whatever's cached in sessionStorage) so credits/tokens are never stale, and hold
  // the chat UI back behind a loader until that call has actually come back.
  useEffect(() => {
    if (!getApiKey()) return;

    let cancelled = false;

    GetUserInfo()
      .then((data) => {
        if (cancelled) return;
        setUser(JSON.stringify(data));
        setUserProfile(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setUserInfoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Only clear the loading state once the URL has actually picked up the new
  // user_id — guarantees the loader renders for at least one paint instead of
  // being flipped on/off synchronously in the same tick as history.replace.
  useEffect(() => {
    if (!authLoading) return;
    const params = new URLSearchParams(location.search);
    if (params.get("user_id")) {
      setAuthLoading(false);
    }
  }, [authLoading, location.search]);

  // Already-logged-in visits with no user_id at all (e.g. a bare /aitutor
  // bookmark) — backfill it from the cached profile, no network call needed.
  useEffect(() => {
    if (!getApiKey()) return;
    const params = new URLSearchParams(location.search);
    if (!params.get("user_id") && user?.id) {
      params.set("user_id", user.id);
      history.replace({ pathname: "/aitutor", search: `?${params.toString()}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const credits = Math.round(
    ((Number(user?.total_tokens) || 0) / TOKENS_PER_MILLION) *
      CREDITS_PER_MILLION_TOKENS
  );

  const handleProfileClick = () => {
    console.log("Profile clicked");
  };

  const handleLogoutClick = () => {
    console.log("Logout clicked");
  };

  // Runs after a guest logs in / signs up from the gate modal — keep the page on a
  // loading state until the freshly authenticated user_id is written into the URL,
  // since ChatSidebar/ChatInterface both key off that query param.
  const handleAuthenticated = (authedUser) => {
    setUserProfile(authedUser);
    setUserInfoLoading(false);
    setLoginModal(false);
    setSignupModal(false);
    setAuthLoading(true);

    const params = new URLSearchParams(window.location.search);
    params.set("user_id", authedUser.id);
    history.replace({ pathname: "/aitutor", search: `?${params.toString()}` });
  };

  const showingAuthGate = loginModal || signupModal;

  return (
    <div className="flex min-h-screen bg-background">
      {showingAuthGate ? (
        <>
          <LoginModal
            role="dialog"
            open={loginModal}
            onClose={() => {}}
            setLoginModal={setLoginModal}
            signup={setSignupModal}
            onAuthenticated={handleAuthenticated}
            focusTrapped={true}
            closeOnOverlayClick={false}
            showCloseIcon={false}
            center
            classNames={{ modal: "react-modal-class1" }}
          />
          <SignUpModal
            role="dialog"
            open={signupModal}
            onClose={() => {}}
            setSignupModal={setSignupModal}
            login={setLoginModal}
            onAuthenticated={handleAuthenticated}
            focusTrapped={true}
            closeOnOverlayClick={false}
            showCloseIcon={false}
            center
            classNames={{ modal: "react-modal-class1" }}
          />
        </>
      ) : authLoading || userInfoLoading ? (
        <FullLoader />
      ) : (
        <>
          <ChatSidebar
            credits={credits}
            onProfileClick={handleProfileClick}
            onLogoutClick={handleLogoutClick}
          />
          <div className="ml-72 flex-1">
            <ChatInterface credits={credits} />
          </div>
        </>
      )}
    </div>
  );
};

export default AITutor;
